import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, A11y } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import api from '../../services/api';
import { openBannerLink } from '../../utils/bannerLink';
import '../../css/Poster.css';
import '../../css/Poster2.css';

// resolve stored /uploads/... paths against the API's origin
const API_ORIGIN = (
  process.env.REACT_APP_API_URL || (api?.defaults?.baseURL || '').replace(/\/api\/?$/, '')
).replace(/\/$/, '');
const resolveMediaUrl = (path) => (!path ? '' : /^https?:\/\//i.test(path) ? path : `${API_ORIGIN}${path}`);

// One request for all three poster slots on the page
let bannersPromise = null;
const loadHeroBanners = () => {
  if (!bannersPromise) {
    bannersPromise = api
      .get('/hero-banners')
      .then((r) => r.data || [])
      .catch((err) => {
        bannersPromise = null;
        console.error('Failed to load hero banners', err);
        return [];
      });
  }
  return bannersPromise;
};

const buttonLabel = (b) => (b.title && b.title.length <= 18 ? `Shop ${b.title.toLowerCase()}` : 'Shop now');
const shortLine = (s) => (s && s.length > 80 ? `${s.slice(0, 78).replace(/\s+\S*$/, '')}…` : s);

export const CarouselArrows = ({ swiper, label = 'slide' }) => (
  <>
    <button type="button" className="m-carousel-arrow is-prev" onClick={() => swiper?.slidePrev()} aria-label={`Previous ${label}`}>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
    </button>
    <button type="button" className="m-carousel-arrow is-next" onClick={() => swiper?.slideNext()} aria-label={`Next ${label}`}>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 18l6-6-6-6" /></svg>
    </button>
  </>
);

// Full-width hero poster for one slot (admin "order" = slot number).
// Several active banners with the same order become a carousel with
// left / right buttons; one banner shows on its own.
const HeroPoster = ({ order, variant = 'poster' }) => {
  const navigate = useNavigate();
  const [slides, setSlides] = useState([]);
  const [swiper, setSwiper] = useState(null);

  useEffect(() => {
    let alive = true;
    loadHeroBanners().then((all) => {
      if (alive) setSlides(all.filter((b) => b.isActive && Number(b.order) === Number(order)));
    });
    return () => {
      alive = false;
    };
  }, [order]);

  if (!slides.length) return null;

  const renderBanner = (banner) => (
    <div className={`${variant}-banner m-poster-full`}>
      <img src={resolveMediaUrl(banner.image)} alt={banner.title || 'Mithralaya collection'} className={`${variant}-banner-image`} loading="lazy" />
      <div className={`${variant}-overlay`} />
      <div className={`${variant}-caption`}>
        {banner.title && <h2 className={`${variant}-heading`}>{banner.title}</h2>}
        {banner.subtitle && <p className="m-poster-sub">{shortLine(banner.subtitle)}</p>}
        <button
          type="button"
          className="m-poster-btn"
          onClick={() => openBannerLink(navigate, banner.ctaPrimaryLink, banner.title, banner.subtitle)}
        >
          {buttonLabel(banner)}
        </button>
      </div>
    </div>
  );

  return (
    <section className={`${variant}-section m-poster-section`} aria-label={slides[0].title || 'Featured collection'}>
      {slides.length === 1 ? (
        renderBanner(slides[0])
      ) : (
        <div className="m-carousel">
          <Swiper
            modules={[Autoplay, Pagination, A11y]}
            onSwiper={setSwiper}
            slidesPerView={1}
            loop
            speed={700}
            autoplay={{ delay: 3000, disableOnInteraction: false, pauseOnMouseEnter: true }}
            pagination={{ clickable: true }}
          >
            {slides.map((b) => (
              <SwiperSlide key={b._id}>{renderBanner(b)}</SwiperSlide>
            ))}
          </Swiper>
          <CarouselArrows swiper={swiper} label="banner" />
        </div>
      )}
    </section>
  );
};

export default HeroPoster;
